"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Circle, ChevronDown, Check, Plus } from "lucide-react";
import { templatesService } from "../apis/templateService";
import { toast } from "react-hot-toast";

export const ACTIVE_TEMPLATE_CHANGED_EVENT = "active-template-changed";
/** Fired while SOAP is regenerating after a template switch: { status, instanceId?, templateName?, message? } */
export const TEMPLATE_REGEN_STATUS_EVENT = "template-regen-status";

export function notifyActiveTemplateChanged(detail = {}) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(ACTIVE_TEMPLATE_CHANGED_EVENT, { detail }),
    );
  }
}

export function notifyTemplateRegenStatus(detail = {}) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(TEMPLATE_REGEN_STATUS_EVENT, { detail }),
    );
  }
}

/**
 * Thin indicator showing which template instance drives SOAP generation.
 * @param {"bar"|"inline"|"none"} variant
 * @param {string|null} [overrideInstanceId] - when set (e.g. template library row), skip fetch
 * @param {boolean} [isActive] - paired with overrideInstanceId for row badge
 * @param {string} [className]
 */
export default function ActiveTemplateIndicator({
  variant = "bar",
  overrideInstanceId = undefined,
  isActive = false,
  className = "",
}) {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(variant !== "inline");

  // Dropdown state (bar variant only)
  const [open, setOpen] = useState(false);
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const dropdownRef = useRef(null);

  const loadActive = useCallback(async () => {
    if (variant === "inline") return;
    try {
      setLoading(true);
      const data = await templatesService.getActiveForSoap();
      setActive(data || null);
    } catch {
      setActive(null);
    } finally {
      setLoading(false);
    }
  }, [variant]);

  const loadInstances = useCallback(async () => {
    try {
      setInstancesLoading(true);
      const data = await templatesService.getMyInstances({ limit: 100 });
      setInstances(Array.isArray(data?.instances) ? data.instances : []);
    } catch {
      setInstances([]);
    } finally {
      setInstancesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActive();
    const onChanged = () => {
      loadActive();
      loadInstances();
    };
    window.addEventListener(ACTIVE_TEMPLATE_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(ACTIVE_TEMPLATE_CHANGED_EVENT, onChanged);
  }, [loadActive, loadInstances]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const toggleDropdown = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) loadInstances();
      return next;
    });
  }, [loadInstances]);

  const handleSelect = useCallback(
    async (instance) => {
      if (!instance?.id || switchingId) return;
      try {
        setSwitchingId(instance.id);
        await templatesService.setAsDefaultForSoap(instance.id);
        notifyActiveTemplateChanged({
          instanceId: instance.id,
          templateName: instance.name,
        });
        setActive({
          instanceId: instance.id,
          name: instance.name,
          ...(active || {}),
        });
        setOpen(false);
        // Regen toast comes from main-content; keep switch feedback light.
        toast.success(`Using "${instance.name}"`, { duration: 1800 });
      } catch (e) {
        toast.error("Failed to change SOAP template");
      } finally {
        setSwitchingId(null);
      }
    },
    [switchingId, active],
  );

  if (variant === "inline") {
    if (!isActive) return null;
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 ${className}`}
        title="This template shapes your AI SOAP notes"
      >
        <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500 shrink-0" />
        Active
      </span>
    );
  }

  if (loading) return null;

  const hasActive = !!active?.name;

  const dropdownMenu = open ? (
    <div className="absolute left-3 right-3 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl">
      <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Choose SOAP template
        </span>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate("/scribe/templates");
          }}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          <Plus className="h-3 w-3" /> Manage
        </button>
      </div>
      {instancesLoading ? (
        <div className="px-3 py-4 text-center text-sm text-gray-500">
          Loading templates…
        </div>
      ) : instances.length === 0 ? (
        <div className="px-3 py-4 text-center text-sm text-gray-500">
          No templates yet.{" "}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/scribe/templates");
            }}
            className="font-medium text-blue-600 hover:underline"
          >
            Create one
          </button>
        </div>
      ) : (
        <ul className="py-1">
          {instances.map((inst) => {
            const selected = active?.instanceId === inst.id;
            return (
              <li key={inst.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(inst)}
                  disabled={!!switchingId}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-emerald-50 disabled:opacity-60 ${
                    selected ? "bg-emerald-50/70" : ""
                  }`}
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="flex-1 truncate text-gray-800">
                    {inst.name}
                    {inst.type ? (
                      <span className="text-gray-400"> · {inst.type}</span>
                    ) : null}
                  </span>
                  {switchingId === inst.id ? (
                    <span className="text-xs text-gray-400">Saving…</span>
                  ) : selected ? (
                    <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div
      ref={dropdownRef}
      className={`relative border-b border-gray-200 bg-white px-3 py-2 ${className}`}
    >
      <button
        type="button"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
          hasActive
            ? "border-emerald-300 bg-emerald-50/80 text-emerald-900 hover:bg-emerald-100/80"
            : "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
        }`}
      >
        <FileText
          className={`h-4 w-4 shrink-0 ${
            hasActive ? "text-emerald-700" : "text-amber-600"
          }`}
        />
        <span className="flex flex-col items-start leading-tight">
          <span className="text-xs font-bold uppercase tracking-wider opacity-70">
            SOAP Template
          </span>
          <span className="font-semibold">
            {hasActive ? (
              <>
                {active.name}
                {active.type ? (
                  <span className="font-normal opacity-75">
                    {" "}
                    · {active.type}
                  </span>
                ) : null}
              </>
            ) : (
              "Select a template"
            )}
          </span>
        </span>
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {dropdownMenu}
    </div>
  );
}
